import { useAuth } from '@clerk/clerk-react'
import { useCallback } from 'react'
import { apiFetch } from '../api'

export type ApiCaller = <T = unknown>(
  path: string,
  opts?: { method?: string; body?: unknown; headers?: Record<string, string> },
) => Promise<T>

export function useApi(): ApiCaller {
  const { getToken } = useAuth()
  return useCallback(
    (path, opts = {}) =>
      apiFetch(path, () => getToken(), {
        method: opts.method,
        body: opts.body,
        headers: opts.headers,
      }),
    [getToken],
  )
}

// One page of a resumable B24 bootstrap (see worker route /b24/bootstrap-*).
export type BootstrapBatch = {
  fetched: number
  inserted: number
  updated: number
  skipped: number
  skippedClosed: number
  // Invoices whose payment this batch booked. Only the reconcile pass settles
  // cash, so the bootstrap routes omit the field.
  settled?: number
  errors: Array<{ bitrix_id: string; reason: string }>
  next: number | null
}

// Aggregate of every batch in a full bootstrap run.
export type BootstrapResult = {
  fetched: number
  inserted: number
  updated: number
  skipped: number
  skippedClosed: number
  settled: number
  errorCount: number
  errorSamples: string[]
  batches: number
  // The payment-reconcile run that follows the import (invoices only). Kept
  // separate rather than summed: it re-reads rows the import already touched, so
  // merging the counters would double-count them and bury the booked payments.
  reconcile?: BootstrapResult
}

/**
 * Drives a resumable bootstrap to completion: the worker processes one bounded
 * chunk per request and returns a `next` cursor; we loop until it is null,
 * accumulating the totals. This keeps each request under the Workers subrequest
 * limit while still importing the whole (scoped) data set in one user action.
 */
export async function runBootstrap(api: ApiCaller, path: string): Promise<BootstrapResult> {
  const total: BootstrapResult = {
    fetched: 0,
    inserted: 0,
    updated: 0,
    skipped: 0,
    skippedClosed: 0,
    settled: 0,
    errorCount: 0,
    errorSamples: [],
    batches: 0,
  }
  const sep = path.includes('?') ? '&' : '?'
  let start: number | null = 0
  while (start !== null) {
    const batch: BootstrapBatch = await api<BootstrapBatch>(`${path}${sep}start=${start}`, {
      method: 'POST',
    })
    total.fetched += batch.fetched
    total.inserted += batch.inserted
    total.updated += batch.updated
    total.skipped += batch.skipped
    total.skippedClosed += batch.skippedClosed
    total.settled += batch.settled ?? 0
    total.errorCount += batch.errors.length
    for (const e of batch.errors) {
      if (total.errorSamples.length < 5) total.errorSamples.push(`${e.bitrix_id}: ${e.reason}`)
    }
    total.batches += 1
    start = batch.next
    if (total.batches > 2000) break // safety valve against a runaway cursor
  }
  return total
}

export type QueryValue = string | number | undefined | null | readonly string[]

export function buildQuery(params: Record<string, QueryValue>): string {
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue
        usp.append(key, String(item))
      }
    } else {
      usp.set(key, String(value))
    }
  }
  const s = usp.toString()
  return s.length === 0 ? '' : `?${s}`
}
